// Pure helpers that turn backend payloads into UI state. Kept free of React
// so they can be unit-tested directly.

export const ASSET_TYPE_TO_TILE = {
  "linear:other-linears": "linear",
  fixed: "fixed",
  "linear:thermoplastic-paint": "lane",
  "electrical-reflective": "night",
};

export const EMPTY_COUNTS = Object.freeze({
  uniqueFiles: 0,
  linear: 0,
  fixed: 0,
  lane: 0,
  night: 0,
  pending: 0,
  processed: 0,
});

const firstField = (list, key) =>
  Array.isArray(list) && list.length > 0 ? list[0]?.[key] : undefined;

// Response of GET /api/getVideoProcessCount
export function parseProcessCount(data) {
  const details = data?.details ?? {};
  const counts = { ...EMPTY_COUNTS };

  counts.uniqueFiles =
    firstField(details.uniqueFilesToday, "UniqueFileCount") ?? 0;
  counts.pending = firstField(details.pendingVideos, "PendingVideos") ?? 0;
  counts.processed =
    firstField(details.processedVideos, "ProcessedVideos") ?? 0;

  const assetCounts = details["Total Asset type Count"];
  if (Array.isArray(assetCounts)) {
    assetCounts.forEach((asset) => {
      const tile = ASSET_TYPE_TO_TILE[asset?.asset_type];
      if (tile) counts[tile] = asset.count ?? 0;
    });
  }
  return counts;
}

// Payload of the socket "refresh" event (JSON string or object).
// Returns null when the payload is unusable. An empty `videos` array is
// returned as [] and applied: the Socket-Server's final refresh carries
// videos: [] once nothing is pending, and it is the source of truth. Until
// 2026-09-23 it was ignored and the grid froze on the last progress value.
export function parseRefresh(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  return {
    progress:
      typeof parsed.completionPercentage === "number"
        ? parsed.completionPercentage
        : undefined,
    remainingMinutes:
      typeof parsed.remainingMinutes === "number"
        ? parsed.remainingMinutes
        : undefined,
    videos: Array.isArray(parsed.videos) ? parsed.videos : undefined,
  };
}

// Response of GET /api/getVideoProcessedDataHistory (and the global variant)
export function parseProcessedHistory(data) {
  return Array.isArray(data?.data) ? data.data : [];
}

export function blockSeverity(block) {
  if (block?.totalErrorVideos > 0) return "danger";
  if (block?.totalNotProcessed > 0 && block?.totalErrorVideos === 0) {
    return "warning";
  }
  return "safe";
}

export function blockRows(block) {
  const rows = [
    { label: "Total", value: block.totalCount },
    { label: "Processed", value: block.totalProcessed },
    { label: "Not Processed", value: block.totalNotProcessed },
    {
      label: "Incorrect Schedule",
      value: block.totalIncorrectScheduleVideos ?? "-",
    },
    { label: "Gps Error", value: block.totalGpsErrorVideos ?? "-" },
    { label: "Idle Video", value: block.totalIdleVideos ?? "-" },
    {
      label: "Video Error",
      value: block.totalVideoErrorVideos ?? "-",
    },
    { label: "Failed", value: block.totalFailedVideos ?? "-" },
  ];
  if (block.site_name)
    rows.push({ label: "Site Name", value: block.site_name });
  if (block.site_id) rows.push({ label: "Site ID", value: block.site_id });
  return rows;
}

export function toggleValue(list, value) {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

export function truncate(text, max) {
  if (typeof text !== "string") return "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

export function describeUploadError(error) {
  if (error?.response) {
    return `Upload failed: ${error.response.status} ${error.response.statusText}`;
  }
  if (error?.request) return "Server not responding.";
  return "Upload failed.";
}

export const UPLOAD_STATUS_LABEL = {
  stored: "Uploaded and registered",
  duplicate: "Duplicate",
  invalid: "Invalid file name",
  failed: "Failed",
};

// A duplicate is answered at once from the rows the video already has; the
// backend sends them as `existing: { state, jobs }` (no row is written and
// the file is kept or put back into SHIFT).
const DUPLICATE_STATE_TEXT = {
  processed: "already processed",
  incomplete: "already processed, uploads pending",
  processing: "already processing",
  queued: "already queued",
  failed: "already uploaded, failed",
  stopped: "already uploaded",
};

export function uploadResultLabel(result) {
  if (result?.status !== "duplicate")
    return UPLOAD_STATUS_LABEL[result?.status] ?? "Failed";
  const text = DUPLICATE_STATE_TEXT[result?.existing?.state];
  return `Duplicate: ${text ?? "already uploaded"}`;
}

// Second line of a duplicate's result: the jobs when they differ from the
// headline (e.g. one failed), and whether the file had to be put back.
export function uploadResultDetail(result) {
  if (result?.status !== "duplicate") return null;
  const parts = [];
  const jobs = Array.isArray(result?.existing?.jobs)
    ? result.existing.jobs
    : [];
  const headline = result?.existing?.state;
  if (jobs.length > 1 && jobs.some((job) => job.state !== headline)) {
    parts.push(
      jobs
        .map((job) => `${job.assetType}: ${job.detail ?? job.state}`)
        .join(" · "),
    );
  } else if (headline === "failed" && jobs[0]?.detail) {
    parts.push(jobs.map((job) => job.detail).join(" · "));
  }
  if (result?.fileRestored)
    parts.push("Video file was missing from SHIFT and has been put back.");
  return parts.length > 0 ? parts.join(" ") : null;
}

// Normalises the backend's upload response (new per-file shape, or the
// older array/message shapes) into one structure the UI can render.
export function summarizeUploadResponse(body, httpStatus, requestedNames = []) {
  const results = [];
  const list = Array.isArray(body?.results)
    ? body.results
    : Array.isArray(body?.data)
      ? body.data
      : [];

  list.forEach((item) => {
    const status = UPLOAD_STATUS_LABEL[item?.status]
      ? item.status
      : item?.records || item?.storedAt
        ? "stored"
        : "failed";
    const existing =
      item?.existing && typeof item.existing === "object"
        ? item.existing
        : null;
    const result = {
      fileName: item?.originalFileName || item?.fileName || "unknown file",
      storedAs: item?.fileName || null,
      status,
      // A duplicate's backend reason repeats the label; it is not an error.
      error: status === "duplicate" && existing ? null : item?.error || null,
      records: Array.isArray(item?.records) ? item.records.length : null,
      existing,
      fileRestored: Boolean(item?.fileRestored),
    };
    results.push({
      ...result,
      label: uploadResultLabel(result),
      detail: uploadResultDetail(result),
    });
  });

  if (Array.isArray(body?.errors)) {
    body.errors.forEach((item) => {
      results.push({
        fileName: item?.fileName || "unknown file",
        storedAs: null,
        status: item?.status === "conflict" ? "duplicate" : "failed",
        error: item?.error || null,
        records: null,
      });
    });
  }

  // Old backends answer with no per-file list at all.
  if (results.length === 0) {
    const ok = httpStatus >= 200 && httpStatus < 300;
    requestedNames.forEach((name) =>
      results.push({
        fileName: name,
        storedAs: null,
        status: ok ? "stored" : httpStatus === 409 ? "duplicate" : "failed",
        error: ok ? null : body?.error || body?.message || `HTTP ${httpStatus}`,
        records: null,
      }),
    );
  }

  const counts = { stored: 0, duplicate: 0, invalid: 0, failed: 0 };
  results.forEach((r) => {
    counts[r.status] += 1;
    if (!r.label) r.label = uploadResultLabel(r);
    if (r.detail === undefined) r.detail = uploadResultDetail(r);
  });
  // Duplicates are not a problem: the video is already in the system.
  const notAdded = counts.invalid + counts.failed;
  const tone =
    results.length === 0
      ? "error"
      : notAdded === 0
        ? "success"
        : counts.stored + counts.duplicate === 0
          ? "error"
          : "warning";
  let message;
  if (results.length === 0) {
    message = body?.message || `Upload failed (HTTP ${httpStatus})`;
  } else if (notAdded === 0 && counts.duplicate === 0) {
    message = `All ${counts.stored} video(s) uploaded and registered.`;
  } else if (notAdded === 0 && counts.stored === 0) {
    message = `All ${counts.duplicate} video(s) were already in the system.`;
  } else {
    message = [
      `${counts.stored} of ${results.length} video(s) registered.`,
      counts.duplicate > 0
        ? `${counts.duplicate} already in the system.`
        : null,
      notAdded > 0 ? `${notAdded} not added.` : null,
    ]
      .filter(Boolean)
      .join(" ");
  }

  return { results, counts, tone, message, httpStatus };
}

const numberOr = (value, fallback) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

// --- Live grid: one card per video file --------------------------------
// The Socket-Server lists one row per job; a day video has two (fixed and
// linear:other-linears), a night video one. The GUI shows one card per
// file: its progress is the mean of the jobs, and Stop targets every job.
export function groupVideosByFile(videos) {
  const groups = new Map();
  (Array.isArray(videos) ? videos : []).forEach((video, index) => {
    const name =
      typeof video?.name === "string" && video.name
        ? video.name
        : `#${video?.id ?? index}`;
    if (!groups.has(name))
      groups.set(name, { key: name, name, ids: [], jobs: [] });
    const group = groups.get(name);
    if (video?.id !== undefined && video?.id !== null) group.ids.push(video.id);
    group.jobs.push({
      id: video?.id ?? null,
      asset_type: video?.asset_type ?? null,
      progress: numberOr(video?.progress, 0),
    });
  });
  return [...groups.values()].map((group) => ({
    ...group,
    progress: Math.round(
      group.jobs.reduce((sum, job) => sum + job.progress, 0) /
        group.jobs.length,
    ),
  }));
}

// --- Day list: rows of GET /api/videos?date= grouped per file ------------
export const JOB_STATE_LABEL = {
  queued: "Queued",
  processing: "Processing",
  processed: "Processed",
  incomplete: "Incomplete",
  failed: "Failed",
  stopped: "Stopped",
};

// The state of one row, using the same rules as the backend history:
// processed needs progress 100 and both uploads done; any error_message
// ("Gps Error", "Idle_Video", "Incorrect Schedule") is a failure; a
// soft-deleted row is a stopped one.
export function jobState(row) {
  if (Number(row?.is_deleted) === 1) return "stopped";
  const error =
    typeof row?.error_message === "string" ? row.error_message.trim() : "";
  if (error) return "failed";
  if (Number(row?.is_processed) === 1) {
    return Number(row.progress_value) === 100 &&
      Number(row.anomaly_uploaded) === 1 &&
      Number(row.site_statics_uploaded) === 1
      ? "processed"
      : "incomplete";
  }
  return Number(row?.progress_value) > 0 ? "processing" : "queued";
}

const STATUS_PRIORITY = [
  "processing",
  "queued",
  "stopped",
  "failed",
  "incomplete",
  "processed",
];

const laterDate = (a, b) => {
  if (!a) return b ?? null;
  if (!b) return a;
  return new Date(b) > new Date(a) ? b : a;
};

// One entry per file. When a job (file + asset type) has several rows, for
// example a stopped one and the row a later upload created, only the newest
// row counts: the older ones are history. `ids` are the rows "Process again"
// sends; `canReprocess` is false while any job is queued or running.
export function groupVideoRows(rows) {
  const newest = new Map(); // "file|asset" -> row
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row || typeof row !== "object") return;
    const key = `${row.file_name ?? ""}|${row.asset_type ?? ""}`;
    const current = newest.get(key);
    if (!current || Number(row.id) > Number(current.id)) newest.set(key, row);
  });

  const groups = new Map();
  [...newest.values()]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .forEach((row) => {
      const name = row.file_name || `#${row.id}`;
      if (!groups.has(name)) {
        groups.set(name, {
          key: name,
          name,
          ids: [],
          jobs: [],
          createdOn: row.created_on ?? null,
          videoDate: row.video_date ?? null,
          dayType: row.day_type ?? null,
          finishedOn: null,
        });
      }
      const group = groups.get(name);
      const state = jobState(row);
      group.ids.push(row.id);
      group.jobs.push({
        id: row.id,
        asset_type: row.asset_type ?? null,
        state,
        progress: numberOr(row.progress_value, 0),
        error:
          (typeof row.error_message === "string" && row.error_message.trim()) ||
          null,
      });
      if (
        state === "processed" ||
        state === "failed" ||
        state === "incomplete"
      ) {
        group.finishedOn = laterDate(
          group.finishedOn,
          row.processed_on ?? row.progressed_on ?? null,
        );
      }
    });

  return [...groups.values()]
    .map((group) => {
      const states = group.jobs.map((job) => job.state);
      const status =
        STATUS_PRIORITY.find((s) => states.includes(s)) ?? "queued";
      const errors = [
        ...new Set(group.jobs.map((job) => job.error).filter(Boolean)),
      ];
      return {
        ...group,
        status,
        errors,
        canReprocess:
          !states.includes("processing") && !states.includes("queued"),
      };
    })
    .sort(
      (a, b) =>
        new Date(b.createdOn ?? 0) - new Date(a.createdOn ?? 0) ||
        (b.ids[0] ?? 0) - (a.ids[0] ?? 0),
    );
}

export function groupStatusLabel(group) {
  switch (group?.status) {
    case "processing": {
      const running = group.jobs.find((job) => job.state === "processing");
      return `Processing ${running?.progress ?? 0}%`;
    }
    case "failed":
      return group.errors?.length
        ? `Failed: ${group.errors.join(", ")}`
        : "Failed";
    default:
      return JOB_STATE_LABEL[group?.status] ?? "Queued";
  }
}

// --- Bell notifications ----------------------------------------------------
// Rows of the `notifications` table joined with the video, either from
// GET /api/notifications ({notifications, unread}) or from the socket
// "bellNotification" event (a JSON string of the same rows).
export function parseNotifications(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const list = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.notifications)
      ? parsed.notifications
      : null;
  if (!list) return null;
  // Blank messages are dropped: the algorithm writes an empty notification
  // on one of its error paths, right before the real one.
  return list
    .filter((item) => item && typeof item === "object")
    .filter(
      (item) => typeof item.message !== "string" || item.message.trim() !== "",
    )
    .map((item) => {
      const message = typeof item.message === "string" ? item.message : "";
      const named = item.file_name ? null : backendMessageSubject(message);
      return {
        id: item.id,
        videoId: item.video_id ?? null,
        message,
        isRead: Number(item.is_read) === 1,
        createdOn: item.created_on ?? null,
        fileName: item.file_name ?? named?.fileName ?? null,
        assetType: item.asset_type ?? named?.assetType ?? null,
      };
    })
    .sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
}

// The backend's own rows ("Stopped <file> (<asset>) at 30%", "Re-queued
// <file> (<asset>)", "Duplicate <file> (<asset>): already processed") are
// written with video_id 0 so that they never block the algorithm's
// one-notification-per-video rule, so they come without a joined file
// name; it is read from the message instead.
const BACKEND_SUBJECT_RE =
  /^(?:Stopped|Re-queued|Duplicate) (.+?) \(([^()]+)\)(?:[ :].*)?$/;

export function backendMessageSubject(message) {
  const m = BACKEND_SUBJECT_RE.exec(String(message ?? "").trim());
  return m ? { fileName: m[1], assetType: m[2] } : null;
}

const KNOWN_KINDS = [
  "processed",
  "stopped",
  "failed",
  "re-queued",
  "duplicate",
];

export function notificationKind(message) {
  const word = String(message ?? "")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase();
  return KNOWN_KINDS.includes(word) ? word : "info";
}

// Consecutive notifications about the same file and of the same kind are
// one bell entry, so a day video's two "Processed" rows read as one video.
export function groupNotifications(list) {
  const entries = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    const kind = notificationKind(item.message);
    const last = entries[entries.length - 1];
    if (
      last &&
      item.fileName &&
      last.fileName === item.fileName &&
      last.kind === kind
    ) {
      last.items.push(item);
      last.unread = last.unread || !item.isRead;
      return;
    }
    entries.push({
      key: item.id,
      fileName: item.fileName,
      kind,
      items: [item],
      latest: item.createdOn,
      unread: !item.isRead,
    });
  });
  return entries;
}

// The message without the file name and asset type the entry already shows:
// "Processed x.MP4 (fixed) in 10 s" -> "Processed in 10 s".
export function shortNotificationText(item) {
  let text = String(item?.message ?? "");
  if (item?.fileName) text = text.split(item.fileName).join("");
  if (item?.assetType) text = text.split(`(${item.assetType})`).join("");
  text = text
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.:;])/g, "$1")
    .trim();
  return text || String(item?.message ?? "");
}

const pad2 = (n) => String(n).padStart(2, "0");

export function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function timeAgo(value, now = Date.now()) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const seconds = Math.round((now - date.getTime()) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return formatDateTime(date);
}
